import 'amplify/config';

import { Authenticator, ThemeProvider } from '@aws-amplify/ui-react';
import ErrorBoundary from 'components/ErrorBoundary';
import { components, formFields } from 'components/forms/AuthComponents';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';
import { amplifyTheme } from 'theme';

import { config } from './config';
import { main } from './configuration';
import { persistor, purgeIfVersionMismatch, store } from './store/setup';
import '@aws-amplify/ui-react/styles.css';

import ErrorFallback from 'components/feedback/ErrorFallback';
import ResetPasswordPage from 'features/reset-password/ResetPasswordPage';
import { useEffect } from 'react';
import { PersistGate } from 'redux-persist/integration/react';
import { router } from 'routes';
import * as Logger from 'services/logger';
import type { z } from 'zod';

import AuthInitializer from './auth/AuthInitializer';
import { authenticatorServices } from './auth/authenticatorServices';
import { EnvSchema } from './envSchema';

const isDev = import.meta.env.DEV;
const isProd = import.meta.env.PROD;
const hasBuildVersion = import.meta.env.VITE_BUILD_VERSION;
const RESET_PASSWORD_ROUTE = '/reset-password';

EnvSchema.parse(import.meta.env);

if (hasBuildVersion) {
    console.info(`[CC] Latest build version ${import.meta.env.VITE_BUILD_VERSION}`);
}

declare global {
    interface ImportMetaEnv extends z.infer<typeof EnvSchema> {
        BASE_URL: string;
        MODE: string;
        DEV: boolean;
        PROD: boolean;
        SSR: boolean;
    }
}

const App = () => {
    useEffect(purgeIfVersionMismatch, []);

    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Provider store={store}>
                <PersistGate persistor={persistor}>
                    <AuthInitializer />
                    <RouterProvider router={router} />
                </PersistGate>
            </Provider>
        </ErrorBoundary>
    );
};

const renderApplication = () => {
    const rootContainer = document.querySelector('#root');
    if (!rootContainer) {
        throw new Error('Root container missing in index.html');
    }

    const root = createRoot(rootContainer);
    const isResetPasswordRoute = window.location.pathname === RESET_PASSWORD_ROUTE;

    root.render(
        <ThemeProvider theme={amplifyTheme}>
            {isResetPasswordRoute ? (
                <ResetPasswordPage />
            ) : (
                <Authenticator
                    components={components}
                    formFields={formFields}
                    services={authenticatorServices}
                    hideSignUp
                >
                    <App />
                </Authenticator>
            )}
        </ThemeProvider>
    );
    Logger.logPageViews('Control Center', Logger.getSessionID());
};

if (isDev) {
    // Without the else the app never mounts when mocks are opted out of, since this was the only
    // dev branch that called `main`.
    if (config.enableMockServer) {
        import('../mocks/browser').then(({ startWorker }) => startWorker()).then(() => main(renderApplication));
    } else {
        main(renderApplication);
    }
}

if (isProd) {
    main(renderApplication);
}
